import React, { useCallback, useEffect, useRef, useState } from "react";
import ProtectedPage from "../../commons/ProtectedPage";
import UnAuthorisedPage from "../../commons/UnAuthorisedPage";
import { GeneralObject } from "../../definitions/general";
import {
  createNewSession,
  getAnonymousToken,
  isAuthorised,
} from "../../utils/auth";
import AuthContext from "./AuthContext";

interface AuthProviderProps {
  name: "website" | "admin" | "chat";
  loginRequired?: boolean;
}

const WEBSITE_CLIENT_ID: string = process.env.REACT_APP_WEBSITE_CLIENT_ID || "";
const ADMIN_CLIENT_ID: string = process.env.REACT_APP_ADMIN_CLIENT_ID || "";
const CHAT_CLIENT_ID: string = process.env.REACT_APP_CHAT_CLIENT_ID || "";

const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  name,
  loginRequired,
}) => {
  const [token, setToken] = useState<string | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<string | null>(null);
  const clientId = useRef<string>(
    name === "website"
      ? WEBSITE_CLIENT_ID
      : name === "admin"
      ? ADMIN_CLIENT_ID
      : CHAT_CLIENT_ID
  );
  const [anonymousToken, setAnonymousToken] = useState<string>("");

  const [isLoggedIn, setLoggedIn] = useState<boolean | null>(null);
  const [sessionId, setSessionId] = useState<string>(createNewSession());
  const [user, setUser] = useState<any>();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    const urlRoles = params.get("roles");
    const urlExpiry = params.get("expiry");
    if (urlToken) {
      try {
        const decodedToken = JSON.parse(atob(urlToken.split(".")[1]));
        const tokenExpiry =
          urlExpiry ||
          (() => {
            const expiryDate = new Date();
            expiryDate.setHours(expiryDate.getHours() + 24);
            return expiryDate.toISOString();
          })();

        localStorage.setItem("zo_token", urlToken);
        localStorage.setItem("zo_token_expiry", tokenExpiry);
        localStorage.setItem(
          "zo_user",
          JSON.stringify({
            id: decodedToken.id,
            first_name: decodedToken.first_name,
            last_name: decodedToken.last_name,
            mobile: decodedToken.mobile,
            app_id: decodedToken.app_id,
            user_id: decodedToken.user_id,
            roles: urlRoles,
          })
        );

        setTokenExpiry(tokenExpiry);
        login(urlToken, tokenExpiry, {
          id: decodedToken.id,
          first_name: decodedToken.first_name,
          last_name: decodedToken.last_name,
          mobile: decodedToken.mobile,
          app_id: decodedToken.app_id,
          user_id: decodedToken.user_id,
          roles: urlRoles,
        });
      } catch (error) {
        console.error("Error processing URL token:", error);
      }
    }
  }, []);

  useEffect(() => {
    getAnonymousToken(clientId.current).then((token) => {
      setAnonymousToken(token);
    });
  }, [clientId]);

  useEffect(() => {
    const localClientId = localStorage.getItem("zo_client_id");
    const sessionClientId = sessionStorage.getItem("zo_client_id");
    if (localClientId || sessionClientId) {
      clientId.current = localClientId || sessionClientId || "";
    }
  }, []);

  useEffect(() => {
    const localToken = localStorage.getItem("zo_token");
    const sessionToken = sessionStorage.getItem("zo_token");
    const _token = sessionToken || localToken;
    if (token) {
      localStorage.setItem("zo_token", token);
    } else {
      if (_token) {
        
        setToken(_token);
      } else {
        setLoggedIn(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const isUser = localStorage.getItem("zo_user");
    if (isUser) {
      try {
        const userObject = JSON.parse(isUser);
        if (userObject) {
          setSessionId(userObject.user_id);
          clientId.current = userObject.app_id;
        }
      } catch (error) {
        console.log("Error parsing user object:", error);
      }
    }
  }, [clientId, sessionId]);

  useEffect(() => {
    const localTokenExpiry = localStorage.getItem("zo_token_expiry");
    const sessionTokenExpiry = sessionStorage.getItem("zo_token_expiry");
    const _tokenExpiry = sessionTokenExpiry || localTokenExpiry;
    if (tokenExpiry) {
      localStorage.setItem("zo_token_expiry", tokenExpiry);
    } else {
      if (_tokenExpiry) {
        setTokenExpiry(_tokenExpiry);
      } else {
        setLoggedIn(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenExpiry]);

  useEffect(() => {
    const localStorageUser = localStorage.getItem("zo_user");
    const sessionStorageUser = sessionStorage.getItem("zo_user");
    const localUser = localStorageUser ? JSON.parse(localStorageUser) : null;
    const sessionUser = sessionStorageUser
      ? JSON.parse(sessionStorageUser)
      : null;
    const _user = sessionUser || localUser;
    if (user) {
      localStorage.setItem("zo_user", JSON.stringify(user));
    } else {
      if (_user) {
        setUser(_user);
        setSessionId(_user.user_id);
      } else {
        setLoggedIn(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user && token && sessionId && tokenExpiry) {
      try {
        if (new Date(tokenExpiry).getTime() < new Date().getTime()) {
          logout();
        }
      } catch (error) {
        console.log(error);
      }
      setLoggedIn(true);
      sessionStorage.removeItem("zo_user");
      sessionStorage.removeItem("zo_token");
      sessionStorage.removeItem("zo_token_expiry");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, sessionId, tokenExpiry]);

  const logout = useCallback(() => {
    localStorage.removeItem("zo_user");
    localStorage.removeItem("zo_token");
    localStorage.removeItem("zo_token_expiry");
    sessionStorage.removeItem("zo_user");
    sessionStorage.removeItem("zo_token");
    sessionStorage.removeItem("zo_token_expiry");
    setLoggedIn(false);
  }, [setLoggedIn]);

  // useEffect(() => {
  //   if (process.env.REACT_APP_ENV === "development") {
  //     loginTest();
  //   }
  // }, []);

  const login = (
    _token: string,
    _tokenExpiry: string,
    _user: GeneralObject
  ) => {
    setToken(_token);
    setTokenExpiry(_tokenExpiry);
    setUser(_user);
  };

  // function loginTest() {
  //   if (
  //     process.env.REACT_APP_DEV_TOKEN &&
  //     process.env.REACT_APP_DEV_TOKEN_EXPIRY &&
  //     process.env.REACT_APP_DEV_USER &&
  //     JSON.parse(process.env.REACT_APP_DEV_USER) != null &&
  //     process.env.REACT_APP_DEV_SESSION_ID
  //   ) {
  //     setToken(process.env.REACT_APP_DEV_TOKEN);
  //     setTokenExpiry(process.env.REACT_APP_DEV_TOKEN_EXPIRY);
  //     setUser(JSON.parse(process.env.REACT_APP_DEV_USER));
  //     setSessionId(process.env.REACT_APP_DEV_SESSION_ID);
  //   }
  // }

  return (
    <AuthContext.Provider
      value={{
        clientId: clientId.current,
        name,
        token,
        isLoggedIn,
        anonymousToken,
        sessionId,
        user,
        logout,
        login,
      }}
    >
      {loginRequired ? (
        isLoggedIn === true ? (
          user && user.roles ? (
            isAuthorised(clientId.current, user.roles) ? (
              children
            ) : (
              <UnAuthorisedPage />
            )
          ) : (
            <UnAuthorisedPage />
          )
        ) : isLoggedIn === false ? (
          <ProtectedPage />
        ) : null
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
